import React, { useEffect, useState, useRef, createElement } from 'react';
import { useForm } from 'react-hook-form';
import { SendIcon, LinkedinIcon, GithubIcon, FileTextIcon, PhoneIcon } from 'lucide-react';
import { contactData } from '../utils/constants';
const Contact = ({
  language = 'en'
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const onSubmit = data => {
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      console.log(data);
      setIsSubmitting(false);
      setIsSubmitted(true);
      reset();
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };
  // Add ripple effect to buttons
  const createRipple = event => {
    const button = event.currentTarget;
    const circle = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.getBoundingClientRect().left - radius}px`;
    circle.style.top = `${event.clientY - button.getBoundingClientRect().top - radius}px`;
    circle.classList.add('ripple');
    const ripple = button.getElementsByClassName('ripple')[0];
    if (ripple) {
      ripple.remove();
    }
    button.appendChild(circle);
  };
  const translations = {
    en: {
      getInTouch: 'Get In',
      touchHighlight: 'Touch',
      name: 'Name',
      namePlaceholder: 'Your name',
      nameRequired: 'Name is required',
      email: 'Email',
      emailPlaceholder: 'Your email',
      emailRequired: 'Email is required',
      invalidEmail: 'Invalid email address',
      message: 'Message',
      messagePlaceholder: 'Your message',
      messageRequired: 'Message is required',
      sending: 'Sending...',
      send: 'Send Message',
      thankYou: 'Thank You!',
      messageSent: "Your message has been sent successfully. I'll get back to you soon!",
      contactInfo: 'Contact Information',
      phone: 'Phone',
      connectWithMe: 'Connect with me',
      downloadResume: 'Download Resume'
    },
    vi: {
      getInTouch: 'Liên',
      touchHighlight: 'Hệ',
      name: 'Họ Tên',
      namePlaceholder: 'Họ tên của bạn',
      nameRequired: 'Vui lòng nhập họ tên',
      email: 'Email',
      emailPlaceholder: 'Email của bạn',
      emailRequired: 'Vui lòng nhập email',
      invalidEmail: 'Địa chỉ email không hợp lệ',
      message: 'Tin Nhắn',
      messagePlaceholder: 'Tin nhắn của bạn',
      messageRequired: 'Vui lòng nhập tin nhắn',
      sending: 'Đang gửi...',
      send: 'Gửi Tin Nhắn',
      thankYou: 'Cảm Ơn Bạn!',
      messageSent: 'Tin nhắn của bạn đã được gửi thành công. Tôi sẽ liên hệ lại với bạn sớm!',
      contactInfo: 'Thông Tin Liên Hệ',
      phone: 'Điện Thoại',
      connectWithMe: 'Kết nối với tôi',
      downloadResume: 'Tải Sơ Yếu Lý Lịch'
    }
  };
  const t = translations[language];
  return <section id="contact" ref={sectionRef} className="w-full py-24 bg-background boston-skyline" data-id="element-68">
      <div className="container mx-auto px-4 relative z-10" data-id="element-69">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16 fade-in" data-id="element-70">
          {t.getInTouch}{' '}
          <span className="gradient-text" data-id="element-71">{t.touchHighlight}</span>
        </h2>
        <div className="flex flex-col md:flex-row gap-8 md:gap-12" data-id="element-72">
          {/* Contact Form */}
          <div className="w-full md:w-2/3 slide-in-left" data-id="element-73">
            <div className="bg-secondary/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 card-glow" data-id="element-74">
              {!isSubmitted ? <form onSubmit={handleSubmit(onSubmit)} data-id="element-75">
                  <div className="mb-6" data-id="element-76">
                    <label htmlFor="name" className="block text-primary mb-2" data-id="element-77">
                      {t.name}
                    </label>
                    <input id="name" type="text" className="w-full bg-background/70 border border-accent-cyan/30 rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent-cyan transition-colors" placeholder={t.namePlaceholder} {...register('name', {
                  required: t.nameRequired
                })} data-id="element-78" />
                    {errors.name && <p className="text-accent-amber mt-1 text-sm" data-id="element-79">
                        {errors.name.message}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-80">
                    <label htmlFor="email" className="block text-primary mb-2" data-id="element-81">
                      {t.email}
                    </label>
                    <input id="email" type="email" className="w-full bg-background/70 border border-accent-cyan/30 rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent-cyan transition-colors" placeholder={t.emailPlaceholder} {...register('email', {
                  required: t.emailRequired,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t.invalidEmail
                  }
                })} data-id="element-82" />
                    {errors.email && <p className="text-accent-amber mt-1 text-sm" data-id="element-83">
                        {errors.email.message}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-84">
                    <label htmlFor="message" className="block text-primary mb-2" data-id="element-85">
                      {t.message}
                    </label>
                    <textarea id="message" rows={5} className="w-full bg-background/70 border border-accent-cyan/30 rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent-cyan transition-colors resize-none" placeholder={t.messagePlaceholder} {...register('message', {
                  required: t.messageRequired
                })} data-id="element-86"></textarea>
                    {errors.message && <p className="text-accent-amber mt-1 text-sm" data-id="element-87">
                        {errors.message.message}
                      </p>}
                  </div>
                  <button type="submit" className="gradient-button px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 w-full ripple-button" disabled={isSubmitting} onClick={createRipple} data-id="element-88">
                    {isSubmitting ? <div className="flex items-center" data-id="element-89">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2" data-id="element-90"></div>
                        <span data-id="element-91">{t.sending}</span>
                      </div> : <>
                        <SendIcon size={20} data-id="element-92" />
                        {t.send}
                      </>}
                  </button>
                </form> : <div className="text-center py-12" data-id="element-93">
                  <div className="w-16 h-16 bg-accent-cyan/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" data-id="element-94">
                    <SendIcon size={32} className="text-accent-cyan" data-id="element-95" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-2" data-id="element-96">
                    {t.thankYou}
                  </h3>
                  <p className="text-muted" data-id="element-97">{t.messageSent}</p>
                </div>}
            </div>
          </div>
          {/* Contact Info */}
          <div className="w-full md:w-1/3 slide-in-right" data-id="element-98">
            <div className="bg-secondary/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 card-glow h-full" data-id="element-99">
              <h3 className="text-2xl font-display font-bold mb-6" data-id="element-100">
                {t.contactInfo}
              </h3>
              <div className="space-y-6" data-id="element-101">
                <div className="transform transition-transform hover:translate-x-2" data-id="element-102">
                  <p className="text-muted mb-1" data-id="element-103">Email</p>
                  <a href="mailto:officialtrungnguyen@gmail.com" className="text-primary hover:text-accent-cyan transition-colors break-all" data-id="element-104">
                    officialtrungnguyen@gmail.com
                  </a>
                </div>
                <div className="transform transition-transform hover:translate-x-2" data-id="element-105">
                  <p className="text-muted mb-1" data-id="element-106">{t.phone}</p>
                  <a href={`tel:${contactData.phone}`} className="text-primary hover:text-accent-cyan transition-colors flex items-center gap-2" data-id="element-107">
                    <PhoneIcon size={16} data-id="element-108" />
                    {contactData.phone}
                  </a>
                </div>
                <div data-id="element-109">
                  <p className="text-muted mb-3" data-id="element-110">{t.connectWithMe}</p>
                  <div className="flex space-x-4" data-id="element-111">
                    <a href="https://www.linkedin.com/in/realtrungnguyen" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-background flex items-center justify-center hover:bg-accent-cyan/20 transition-all duration-300 hover:scale-110" data-id="element-112">
                      <LinkedinIcon size={20} className="text-primary" data-id="element-113" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-background flex items-center justify-center hover:bg-accent-cyan/20 transition-all duration-300 hover:scale-110" data-id="element-114">
                      <GithubIcon size={20} className="text-primary" data-id="element-115" />
                    </a>
                  </div>
                </div>
                <div className="transform transition-transform hover:translate-x-2" data-id="element-116">
                  <a href="https://drive.google.com/file/d/1uaQqtMCqQRsS2yBadyDQ0cmCFu7Edq3t/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary hover:text-accent-cyan transition-colors" data-id="element-117">
                    <FileTextIcon size={20} data-id="element-118" />
                    {t.downloadResume}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default Contact;